import React from 'react';
import { Box, Container, Typography, Button, Stack, Divider } from '@mui/material';
import Grid from '@mui/material/Grid';
import Header from '../Header';
import SEO from '../SEO';
import Footer from '../Footer';
import { useNavigate } from 'react-router-dom';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 6 }}>
      <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.8rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function Para({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontSize: { xs: 15, md: 17 }, color: '#9499b0', lineHeight: 1.8, mb: 2 }}>
      {children}
    </Typography>
  );
}

export default function BlogDisputedAllegationsPage() {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO title="Disputed Allegations at Rainbow Childcare — How Evidence Resolves False Claims" description="When a daycare worker is falsely accused, tamper-evident footage resolves the dispute immediately. A case study in both outcomes." path="/blog/disputed-allegations-rainbow-childcare" type="article" />
      <Header onBookDemo={() => navigate('/contact')} />
      <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 }, px: 3 }}>

        {/* Breadcrumb */}
        <Box sx={{ display: 'flex', gap: 1, mb: 4, alignItems: 'center' }}>
          <Typography onClick={() => navigate('/blog')} sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', cursor: 'pointer', '&:hover': { color: '#5ce1e6' } }}>Blog</Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: 'rgba(148,153,176,0.4)' }}>›</Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0' }}>Trust & Transparency</Typography>
        </Box>

        {/* Tag badge */}
        <Box sx={{ px: 1.5, py: 0.5, bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.2)', borderRadius: 1, display: 'inline-block', mb: 2.5 }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 11, color: '#5ce1e6', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Trust & Transparency · 6 min read</Typography>
        </Box>

        <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.8rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', mb: 3, lineHeight: 1.1 }}>
          "It Never Happened." When a Daycare and a Parent Disagree, Only One Thing Settles It.
        </Typography>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, color: '#9499b0', mb: 5, lineHeight: 1.65 }}>
          A mother said her child left a Hilliard daycare with six fractured bones and five bruises. The daycare owner said it never happened — and that cameras had been running the whole time. The mother said the cameras were down for maintenance. Twelve thousand people shared the post before anyone reviewed a single frame. This case is not about caught abuse. It is about what happens when the truth has no witness.
        </Typography>

        {/* YouTube embed */}
        <Box sx={{ position: 'relative', paddingTop: '56.25%', borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)', mb: 2, boxShadow: '0 0 40px rgba(0,0,0,0.4)' }}>
          <Box
            component="iframe"
            src="https://www.youtube.com/embed/a9iT4EcvZVM"
            title="Mother accuses daycare of abusing child; daycare operator says it never happened — NBC4 Columbus"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
          />
        </Box>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: 'rgba(148,153,176,0.5)', mb: 5, textAlign: 'center' }}>
          NBC4 Columbus — original coverage of the Rainbow Child Care, Too allegation, Hilliard, Ohio
        </Typography>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 5 }} />

        <Section title="July 26, 2017 — a post, 12,000 shares, and no footage">
          <Para>
            On the morning of July 26, 2017, Crystal Gilmore posted to Facebook with a serious allegation: her daughter had left Rainbow Child Care, Too — a licensed daycare in Hilliard, Ohio, operating for 13 years — with six fractured bones and five bruises. The post spread fast. By the time NBC4 ran the story days later, it had been shared more than 12,000 times.
          </Para>
          <Para>
            Tamara Marinescu, who owns the center with her husband, responded with equal conviction. Her daycare, she said, was under 24-hour surveillance. In 13 years of operation, no one had ever made an allegation like this. "Oh my god no," she told NBC4. "If I thought for an instant that they would have done this, they would not be employed here."
          </Para>
          <Para>
            Gilmore, at the advice of her attorney, declined to speak with NBC4. But in her Facebook post, she included a detail that made resolution impossible: the security cameras, she alleged, had been down for maintenance during the period when the injuries supposedly occurred.
          </Para>
          <Para>
            Columbus police opened an investigation. The Ohio Department of Job and Family Services reviewed the facility's inspection records and found no prior serious violations. And then, in the way that many disputed daycare cases end, the story quietly disappeared from public view — no charges filed, no footage produced, no resolution anyone could point to as definitive.
          </Para>
          <Para>
            What remained was the post. Twelve thousand shares. And a daycare owner who spent years rebuilding what a single unverified allegation almost destroyed.
          </Para>
        </Section>

        <Section title="Two people telling the truth. Or one of them lying. And no way to know.">
          <Para>
            This is the scenario that never makes the evening news but plays out in childcare facilities across the country every week: a parent sees something, or a child says something, or a child comes home with a mark that wasn't there in the morning — and the daycare says it didn't happen.
          </Para>
          <Para>
            In the absence of objective evidence, what follows is a collision of perspectives that no amount of goodwill can resolve. The parent is not wrong to worry. The daycare owner is not wrong to defend herself. Both can be completely sincere, and still the truth remains inaccessible to either of them.
          </Para>

          {/* Two sides split view */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ p: 3, bgcolor: '#13131f', border: '1px solid rgba(255,107,107,0.15)', borderTop: '3px solid rgba(255,107,107,0.5)', borderRadius: 2, height: '100%' }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 13, color: '#ff6b6b', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>What the parent needs</Typography>
                <Stack spacing={1}>
                  {[
                    'Proof that harm occurred — or didn\'t',
                    'A record of exactly who was with her child, and when',
                    'Confidence that her child is safe going back',
                    'An answer that doesn\'t depend on who she believes',
                  ].map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                      <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#ff6b6b', mt: 0.7, flexShrink: 0 }} />
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{item}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ p: 3, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.15)', borderTop: '3px solid rgba(92,225,230,0.5)', borderRadius: 2, height: '100%' }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 13, color: '#5ce1e6', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>What the daycare owner needs</Typography>
                <Stack spacing={1}>
                  {[
                    'Proof that it didn\'t happen — not just her word',
                    'A timestamped audit trail she can show police, CPS, and parents',
                    'Protection from a viral post before an investigation concludes',
                    'Thirteen years of good work defended by evidence, not reputation',
                  ].map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                      <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#5ce1e6', mt: 0.7, flexShrink: 0 }} />
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{item}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>

          <Para>
            In the Rainbow Child Care case, neither side had what they needed. The parent had a viral post and an attorney. The owner had 13 years of clean inspections and a camera system she claimed was running. And the cameras, according to the allegation, had been down at exactly the moment that mattered.
          </Para>
        </Section>

        <Section title="The cameras were down for maintenance">
          <Para>
            That phrase — "the cameras were down for maintenance" — is one of the most consequential sentences in a disputed childcare case. It doesn't say what happened. It says that whatever happened, there is no record of it.
          </Para>
          <Para>
            Traditional surveillance systems have this vulnerability by design. They are hardware-first: cameras that record to local storage, storage that can be wiped, systems that require maintenance windows, connections that drop. "The cameras were down" is a claim that is structurally possible in any conventional setup — and structurally impossible to disprove without the footage that would have disproved it.
          </Para>
          <Para>
            This is not a theoretical edge case. Maintenance windows, power outages, storage failures, and deliberate tampering are recurring features of disputed daycare cases. In several of the most serious abuse cases on record, the perpetrator's ability to control or disable the recording system was part of how the abuse continued undetected.
          </Para>

          <Box sx={{ p: 2.5, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.15)', borderLeft: '3px solid #5ce1e6', borderRadius: 2, mb: 3 }}>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#f0f0f8', mb: 0.75 }}>
              With SMCLA, "the cameras were down" is not a sentence that can be said.
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>
              SMCLA's footage is continuously synced to cloud storage. There are no local maintenance windows that erase a record. There is no single point of failure that produces a gap. The audit trail is continuous, timestamped, and tamper-evident — which means both sides always have access to the same objective record.
            </Typography>
          </Box>
        </Section>

        <Section title="The reputational math of an unresolved allegation">
          <Para>
            Twelve thousand shares happened before any investigation concluded. That is the math of social media and disputed allegations: the accusation travels at viral speed, and the correction — if it ever comes — travels at the speed of a news cycle that has already moved on.
          </Para>
          <Para>
            Tamara Marinescu had operated Rainbow Child Care, Too for 13 years. The Ohio Department of Job and Family Services had no record of serious violations. She had, by any reasonable measure, built a facility that operated with integrity. None of that was visible to the parents who saw 12,000 shares of an allegation and made a different calculation about where to send their children.
          </Para>
          <Para>
            This is not a unique situation. It is the standard outcome of a disputed daycare allegation in the absence of objective evidence: the accused facility absorbs the reputational cost of the uncertainty, regardless of what actually happened.
          </Para>
          <Para>
            An operator with SMCLA running does not face this exposure. Within hours of an allegation, they can produce continuous timestamped footage covering the period in question — footage that is stored independently, not on facility hardware that could be claimed to have been offline. If no abuse occurred, the footage shows it. If abuse did occur, the footage also shows it — and the system would have already alerted someone before the parent had to call anyone.
          </Para>
        </Section>

        <Section title="Why this case is different — and why it matters more">
          <Para>
            Every other case on this blog involves confirmed abuse: a worker who confessed, footage that showed the acts, charges that were filed. Those cases are straightforward arguments for detection technology. If the AI had been running, it would have flagged the behavior. The abuse would have stopped sooner. Children would have been protected.
          </Para>
          <Para>
            This case is different. We do not know what happened at Rainbow Child Care, Too on the day in question. We may never know. That uncertainty is precisely what makes it the most honest argument for what SMCLA actually provides.
          </Para>
          <Para>
            Because SMCLA is not just an abuse detection system. It is an objective record-keeping system that replaces the word of one person against another with a continuous, tamper-evident audit trail. It does not take sides. It does not have a stake in the outcome. It records what happened and makes that record accessible — to parents who need answers, to operators who need to defend themselves, and to investigators who need evidence.
          </Para>

          <Box sx={{ p: 3, bgcolor: 'rgba(92,225,230,0.04)', border: '1px solid rgba(92,225,230,0.15)', borderRadius: 2, mb: 3 }}>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 16, color: '#5ce1e6', mb: 2, textAlign: 'center' }}>
              Two scenarios. One system. Both sides protected.
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ p: 2.5, bgcolor: '#13131f', borderRadius: 2, border: '1px solid rgba(255,255,255,0.06)' }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 13, color: '#ff6b6b', mb: 0.75 }}>If abuse occurred</Typography>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>
                  SMCLA detects the behavioral event in real time and alerts the owner within 60 seconds. The child is protected before the parent ever has to make a police report. The footage is preserved as legal-grade evidence. The parent never has to fight to be believed.
                </Typography>
              </Box>
              <Box sx={{ p: 2.5, bgcolor: '#13131f', borderRadius: 2, border: '1px solid rgba(255,255,255,0.06)' }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 13, color: '#5ce1e6', mb: 0.75 }}>If abuse did not occur</Typography>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>
                  The owner produces continuous footage for the period in question — stored off-site, tamper-evident, timestamped. The allegation is addressed with evidence, not denial. The business is protected. The operator does not absorb the reputational cost of an unresolved dispute.
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Section>

        <Section title="What SMCLA gives both sides">
          <Para>
            We built SMCLA because we are parents. We understand what it means to wonder whether your child is safe — and to have no objective way to answer that question. We also understand what it means for a daycare operator who has done everything right to face an allegation they cannot disprove.
          </Para>
          <Para>
            Most surveillance technology is positioned as a tool for catching bad actors. SMCLA does that. But what it also does — and what no other system on the market is designed to do simultaneously — is protect the operators who are running good facilities.
          </Para>
          <Para>
            The operator with SMCLA running can tell every parent: you don't have to take my word for it. You don't have to trust my staff. You don't have to hope the cameras were on. There is a continuous, independently stored, tamper-evident record of every room in this facility, every shift, every day. If you ever have a concern, I can show you the footage within the hour.
          </Para>
          <Para>
            That is not just a safety system. It is a business proposition that no competitor can match with a verbal assurance and a sign that says "under 24-hour surveillance."
          </Para>
        </Section>

        {/* Source attribution */}
        <Box sx={{ p: 2.5, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, mb: 5 }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 12, color: '#9499b0', mb: 1.5 }}>Sources</Typography>
          <Stack spacing={1}>
            {[
              { label: 'NBC4 Columbus — Mother accuses daycare of abusing child; daycare owner says it never happened', url: 'https://www.nbc4i.com/news/mother-accuses-daycare-of-abusing-child-daycare-owner-says-it-never-happened/' },
              { label: '614NOW — Hilliard daycare being accused of abusing baby girl', url: 'https://614now.com/2017/hot-topics/news/hilliard-daycare-being-accused-of-abusing-baby-girl' },
            ].map(({ label, url }) => (
              <Box key={url} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'rgba(148,153,176,0.4)', mt: 0.7, flexShrink: 0 }} />
                <Typography
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: '#9499b0', lineHeight: 1.5, textDecoration: 'none', '&:hover': { color: '#5ce1e6', textDecoration: 'underline' } }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 5 }} />

        <Box sx={{ p: 4, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.2)', borderRadius: 3, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 20, color: '#f0f0f8', mb: 1 }}>
            Give parents proof. Give operators protection.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', mb: 3, lineHeight: 1.65 }}>
            SMCLA gives both sides the same thing: an objective, continuous, tamper-evident record that replaces "I said / she said" with footage that speaks for itself. No maintenance windows. No gaps. No ambiguity.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button variant="contained" onClick={() => navigate('/contact')} sx={{ px: 4, py: 1.5, fontWeight: 700, fontSize: 15, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, '&:hover': { bgcolor: '#4ecfd4' } }}>
              Book a Demo →
            </Button>
            <Button variant="outlined" onClick={() => navigate('/privacy')} sx={{ px: 4, py: 1.5, fontWeight: 600, fontSize: 15, color: '#f0f0f8', borderColor: 'rgba(255,255,255,0.15)', borderRadius: 2, '&:hover': { borderColor: 'rgba(255,255,255,0.35)', bgcolor: 'rgba(255,255,255,0.04)' } }}>
              How we protect privacy →
            </Button>
          </Stack>
        </Box>
      </Container>
      <Footer />
    </Box>
  );
}
